import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container } from '@/components/container';
import { KeenIcon } from '@/components';
import { useSnackbar } from 'notistack';
import {
  useGetMarinaSubscriptionTransactionDetailQuery,
  useDownloadMarinaSubscriptionTransactionReceiptMutation,
  useEmailMarinaSubscriptionTransactionReceiptMutation,
} from '@/services/SubscriptionTransactionsService';
import { isSubscriptionTx } from '@/types/transactions.types';
import { MarinaSubscriptionTransactionResponse } from '@/types/subscription-transactions.types';
import { formatFinnishCurrency } from '@/utils';
import { formatDisplayDateTime } from '@/utils/Date';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { BillingCycle } from '@/types/subscription-management.types';

const formatTaxRegime = (value?: string | null): string => {
  if (!value) return '—';
  return value
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

const formatDiscountType = (value?: string | null): string => {
  if (!value) return '—';
  if (value === 'fixed') return 'Fixed amount';
  if (value === 'percentage') return 'Percentage';
  return value;
};

const formatDiscountValue = (
  type?: string | null,
  value?: number | null,
  currency = 'EUR'
): string => {
  if (value == null) return '—';
  if (type === 'percentage') return `${value}%`;
  return formatFinnishCurrency(value, { currency });
};

const formatBillingCycle = (val: unknown): string => {
  const num = typeof val === 'number' ? val : Number(val);
  if (num === BillingCycle.MONTHLY) return 'Monthly';
  if (num === BillingCycle.YEARLY) return 'Annual';
  if (num === BillingCycle.FIVE_DAYS) return '5 Days';
  return String(val ?? '—');
};

const extractRtkQueryErrorMessage = (err: unknown, fallback: string): string => {
  const e = err as {
    data?: { message?: unknown; details?: unknown };
    error?: unknown;
    message?: unknown;
  } | null;
  if (e?.data && typeof e.data === 'object') {
    const details = typeof e.data.details === 'string' ? e.data.details : '';
    const message = typeof e.data.message === 'string' ? e.data.message : '';
    if (message === 'HARBOR_BUSINESS_ENTITY_DATA_INCOMPLETE' && details) {
      return details;
    }
    if (message) return message;
    if (details) return details;
  }
  if (typeof e?.error === 'string' && e.error) return e.error;
  if (typeof e?.message === 'string' && e.message) return e.message;
  return fallback;
};

const SubscriptionTransactionDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const userData = useSelector((state: RootState) => state.auth.userData);
  const [email, setEmail] = useState(userData?.email || '');
  const [isDownloading, setIsDownloading] = useState(false);

  const {
    data: transaction,
    isLoading,
    error,
  } = useGetMarinaSubscriptionTransactionDetailQuery(id || '', {
    skip: !id,
  });

  const [downloadReceipt] =
    useDownloadMarinaSubscriptionTransactionReceiptMutation();
  const [emailReceipt, { isLoading: isEmailSending }] =
    useEmailMarinaSubscriptionTransactionReceiptMutation();

  useEffect(() => {
    if (userData?.email && !email) {
      setEmail(userData.email);
    }
  }, [userData?.email, email]);

  useEffect(() => {
    if (!error) return;
    const err = error as unknown as {
      data?: unknown;
      error?: string;
    } | null;
    let message = 'Failed to fetch subscription transaction details';
    if (
      err?.data &&
      typeof err.data === 'object' &&
      'message' in (err.data as Record<string, unknown>)
    ) {
      const m = (err.data as { message?: string }).message;
      if (typeof m === 'string' && m) message = m;
    } else if (typeof err?.error === 'string' && err.error) {
      message = err.error;
    }
    enqueueSnackbar(message, { variant: 'error' });
  }, [error, enqueueSnackbar]);

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'SUCCEEDED':
        return 'badge-success';
      case 'FAILED':
        return 'badge-danger';
      case 'REFUNDED':
      case 'PARTIALLY_REFUNDED':
        return 'badge-warning';
      default:
        return 'badge-secondary';
    }
  };

  const canDownloadReceipt = ['SUCCEEDED', 'REFUNDED', 'PARTIALLY_REFUNDED'].includes(
    String(transaction?.data?.status || '').toUpperCase()
  );

  const handleDownloadReceipt = async () => {
    if (!id) return;
    try {
      setIsDownloading(true);
      const blob = await downloadReceipt(id).unwrap();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `subscription-transaction-${id}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
      enqueueSnackbar('Receipt downloaded successfully', {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(
        extractRtkQueryErrorMessage(err, 'Failed to download receipt'),
        { variant: 'error' }
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const handleEmailReceipt = async () => {
    if (!id || !email.trim()) {
      enqueueSnackbar('Please enter a valid email address', {
        variant: 'warning',
      });
      return;
    }
    try {
      await emailReceipt({ data: { id, email: email.trim() } }).unwrap();
      enqueueSnackbar('Receipt email sent successfully', {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(
        extractRtkQueryErrorMessage(err, 'Failed to send receipt email'),
        { variant: 'error' }
      );
    }
  };

  const renderDiscountDetails = (tx: MarinaSubscriptionTransactionResponse) => {
    if (!tx.data.discount_applied) return null;

    return (
      <div className="card mt-6">
        <div className="card-body">
          <h2 className="card-title mb-4">Discount Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Discount code</p>
              <p className="font-medium">{tx.data.discount_code || '—'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Discount label</p>
              <p className="font-medium">{tx.data.discount_label || '—'}</p>
            </div>
            <div className="md:col-span-2">
              <p className="text-sm text-gray-500">Description</p>
              <p className="font-medium">
                {tx.data.discount_description || '—'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Discount type</p>
              <p className="font-medium">
                {formatDiscountType(tx.data.discount_type)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Discount value</p>
              <p className="font-medium">
                {formatDiscountValue(
                  tx.data.discount_type,
                  tx.data.discount_value,
                  tx.data.currency
                )}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Discount amount</p>
              <p className="font-medium">
                {tx.data.discount_amount != null
                  ? formatFinnishCurrency(tx.data.discount_amount, {
                      currency: tx.data.currency,
                    })
                  : '—'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Original price</p>
              <p className="font-medium">
                {tx.data.original_gross_amount != null
                  ? formatFinnishCurrency(tx.data.original_gross_amount, {
                      currency: tx.data.currency,
                    })
                  : tx.data.original_amount != null
                    ? formatFinnishCurrency(tx.data.original_amount, {
                        currency: tx.data.currency,
                      })
                    : '—'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Final price</p>
              <p className="font-medium">
                {tx.data.final_gross_amount != null
                  ? formatFinnishCurrency(tx.data.final_gross_amount, {
                      currency: tx.data.currency,
                    })
                  : tx.data.final_amount != null
                    ? formatFinnishCurrency(tx.data.final_amount, {
                        currency: tx.data.currency,
                      })
                    : formatFinnishCurrency(tx.data.amount, {
                        currency: tx.data.currency,
                      })}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSubscriptionDetails = (
    tx: MarinaSubscriptionTransactionResponse
  ) => {
    if (!isSubscriptionTx(tx)) return null;

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-body">
            <h2 className="card-title mb-4">Subscription Details</h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Subscription Name</p>
                <div
                  className="prose max-w-none font-medium dark:text-white contrast:text-white"
                  dangerouslySetInnerHTML={{ __html: tx.owner.data.name }}
                />
              </div>
              <div>
                <p className="text-sm text-gray-500">Subtitle</p>
                <p className="font-medium">{tx.owner.data.name_sub_title}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Price</p>
                <p className="font-medium">
                  {formatFinnishCurrency(tx.owner.data.price)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Billing Cycle</p>
                <p className="font-medium">{formatBillingCycle(tx.owner.data.billing_cycle)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <h2 className="card-title mb-4">Harbor & Invoice</h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Harbor</p>
                <p className="font-medium">{tx.data.harbor_name || '—'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Netvisor Invoice ID</p>
                <p className="font-mono text-sm break-all">
                  {tx.data.netvisor_invoice_id || '—'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Tax Regime</p>
                <p className="font-medium">
                  {formatTaxRegime(tx.data.tax_regime || null)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <Container>
        <h1 className="sr-only">Subscription Transaction Details</h1>
        <div className="flex items-center justify-center min-h-[300px]">
          <span className="loading loading-spinner loading-lg" />
        </div>
      </Container>
    );
  }

  if (!transaction) {
    return (
      <Container>
        <h1 className="sr-only">Subscription Transaction Details</h1>
        <div className="card">
          <div className="card-body text-center py-12">
            <p className="text-gray-500">Subscription transaction not found</p>
            <button
              className="btn btn-primary mt-4"
              onClick={() => navigate('/subscription-transactions')}
            >
              Back to list
            </button>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button
            className="btn btn-sm btn-ghost"
            onClick={() => navigate('/subscription-transactions')}
            aria-label="Back to subscription transactions"
          >
            <KeenIcon icon="arrow-left" className="text-lg" />
          </button>
          <h1 className="text-xl font-semibold">
            Subscription Transaction Details
          </h1>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-primary btn-sm gap-2"
            onClick={handleDownloadReceipt}
            disabled={isDownloading || !canDownloadReceipt}
          >
            <KeenIcon icon="file-down" className="text-base" />
            {isDownloading ? 'Downloading...' : 'Download Receipt'}
          </button>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-body">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="font-mono text-sm break-all">
              {transaction.data.id}
            </span>
            <span
              className={`badge ${getStatusBadgeClass(transaction.data.status)}`}
            >
              {transaction.data.status}
            </span>
            <span className="text-sm text-gray-500">
              {formatDisplayDateTime(new Date(transaction.data.created_at))}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-base-200 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">Amount</p>
              <p className="text-lg font-semibold">
                {formatFinnishCurrency(transaction.data.amount, {
                  currency: transaction.data.currency,
                })}
              </p>
            </div>
            <div className="bg-base-200 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">Refunded</p>
              <p className="text-lg font-semibold">
                {formatFinnishCurrency(transaction.data.refunded_amount, {
                  currency: transaction.data.currency,
                })}
              </p>
            </div>
            <div className="bg-base-200 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">Currency</p>
              <p className="text-lg font-semibold">
                {transaction.data.currency}
              </p>
            </div>
            <div className="bg-base-200 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">Harbor</p>
              <p className="text-lg font-semibold">
                {transaction.data.harbor_name || '—'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {renderSubscriptionDetails(transaction)}
      {renderDiscountDetails(transaction)}

      <div className="card mt-6">
        <div className="card-body">
          <h2 className="card-title mb-4">Email Receipt</h2>
          <div className="flex flex-col md:flex-row gap-3">
            <input
              type="email"
              className="input input-bordered flex-1"
              placeholder="Recipient email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleEmailReceipt}
              disabled={isEmailSending || !canDownloadReceipt}
            >
              {isEmailSending ? 'Sending...' : 'Send Receipt'}
            </button>
          </div>
        </div>
      </div>

      <div className="card mt-6">
        <div className="card-body">
          <h2 className="card-title mb-4">Payment Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {transaction.data.payment_intent_id && (
              <div>
                <p className="text-sm text-gray-500">Stripe Payment Intent</p>
                <p className="font-mono text-sm break-all">
                  {transaction.data.payment_intent_id}
                </p>
              </div>
            )}
            {transaction.data.paytrail_payment_id && (
              <div>
                <p className="text-sm text-gray-500">Paytrail Payment ID</p>
                <p className="font-mono text-sm break-all">
                  {transaction.data.paytrail_payment_id}
                </p>
              </div>
            )}
            {transaction.data.stripe_refund_id && (
              <div>
                <p className="text-sm text-gray-500">
                  {transaction.data.payment_provider?.toLowerCase() ===
                    'paytrail' || transaction.data.paytrail_payment_id
                    ? 'Paytrail Refund ID'
                    : 'Stripe Refund ID'}
                </p>
                <p className="font-mono text-sm break-all">
                  {transaction.data.stripe_refund_id}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Container>
  );
};

export default SubscriptionTransactionDetails;
